/**
 * Shared price-click interception for V1 moneylines and LT2 derivatives.
 *
 * Every spread/total surface (dashboard board, GameDetail Spread/Total,
 * GameDetail Game Lines) funnels pill + ladder clicks into a
 * `TradeTicketContext`. When the toolbar 1-Click control is ARMED and the
 * trader holds both capabilities, `tryOneClickFromTicketCtx` converts that
 * ctx into a normalized `LiveOrderIntent` and submits through
 * `oneClickStore.submitIntent` — the same audited placeOrder lane, behind the
 * same submit-time guard — instead of opening the ticket.
 *
 * Identity rule (LT2-006): the ticker is resolved from the clicked line's OWN
 * synth VenueBook (`ctx.game.kalshi`), never by re-finding a row in a games
 * list. A synth GameRow carries exactly one underlying market in both slots.
 *
 * Returns true when the click was CONSUMED (order submitted or truthfully
 * refused by the guard — either way the ticket must not open). Returns false
 * when the click is not one-click eligible; the caller falls through to the
 * ticket AND disarms, the same V1 safety boundary handleUserTradeClick keeps
 * ("leaving the hot path disarms").
 */
import { useAuthStore } from '@/stores/authStore';
import { hasConfirmedGrant } from '@/stores/grants';
import { useOneClickStore } from '@/stores/oneClickStore';
import { useLt2FocusStore } from '@/stores/lt2FocusStore';
import { lt2MoneylineGameCardId, type Lt2MoneylineCardScope } from './lt2Moneyline';
import type { LiveOrderIntent, Lt2Period, Lt2Surface } from '@/lib/trading/liveIntent';
import { lt2ClockStamp } from '@/lib/trading/lt2Timing';
import type {
  GameRow,
  PriceMode,
  SeriesOutcome,
  TradeTicketContext,
  VenueBook,
} from './types';
import { gameMarketKind } from './tradeModalBook';
import { probeStart } from '@/lib/trading/orderLatencyProbe';

export interface OneClickInterceptArgs {
  ctx: TradeTicketContext;
  surface: Lt2Surface;
  period: Lt2Period;
  priceMode: PriceMode;
  focusedCardId?: string | null;
}

export interface NormalizedOneClickArgs {
  intent: LiveOrderIntent;
  book: VenueBook | null | undefined;
  /** Derivative surfaces require the separate LT2 grant. Moneyline and
   *  N-outcome moneyline surfaces retain the established V1 one-click grant. */
  capability: 'one-click' | 'lt2';
  /** The hotkey card id the clicked price belongs to, when the caller can
   *  prove it. Consumed ONLY by the owner-gated readiness blur — never by
   *  the guard chain. Omitted/null = never blur (fence retained). */
  ownerCardId?: string | null;
}

/**
 * The single adapter from a fully identified rendered price click into the
 * guarded store primitive. Call sites still own presentation-specific
 * identity and labels; this function owns the capability matrix and prevents
 * each surface from inventing a different submission route.
 *
 * `true` means the armed click was consumed, including a guard refusal that
 * produced a durable acknowledgement. `false` means the caller must preserve
 * the ordinary ticket path and disarm at that boundary.
 */
export function tryOneClickFromNormalizedIntent({
  intent,
  book,
  capability,
  ownerCardId,
}: NormalizedOneClickArgs): boolean {
  const store = useOneClickStore.getState();
  const user = useAuthStore.getState().user;
  if (!store.armed) return false;
  if (!hasConfirmedGrant(user, 'oneClickAccess')) return false;
  if (capability === 'lt2' && !hasConfirmedGrant(user, 'lt2Access')) return false;

  // `submitIntent` revalidates identity, availability, price, and maker role
  // against this exact current book. A null/unavailable book is still
  // consumed truthfully by that guard; it never becomes a ticket fallback
  // that looks like an armed click silently did nothing.
  probeStart('mouse', intent.label); // M3b: the click-path twin of the hotkey stamp
  void store.submitIntent(intent, book);
  // A consumed armed click leaves DOM focus on the price button it landed
  // on, and the hotkey fence rightly treats a focused button as "owned" —
  // the very next A/D press got rejected with "Focus is on another control"
  // (the Phase 3 Loom flow). Release that incidental focus ONLY when the
  // clicked price provably belongs to the card the hotkeys are aimed at:
  // blurring after a click on a DIFFERENT card would let the next A/D fire
  // at a card the trader just looked away from, which is worse than the
  // rejection. Cross-card clicks keep the fence; the explicit focus chip is
  // the sanctioned way to retarget. Inputs/menus are never touched — only a
  // BUTTON, and only on an owner match.
  if (ownerCardId != null && ownerCardId === useLt2FocusStore.getState().focusedCardId) {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.tagName === 'BUTTON') active.blur();
  }
  return true;
}

export interface MoneylineOneClickArgs {
  ctx: TradeTicketContext;
  /** The hosting moneyline surface's card scope — provides both the intent's
   *  surface stamp and the keyPrefix that names the clicked game's shared
   *  hotkey card id (the owner-match input for the readiness blur above). */
  scope: Lt2MoneylineCardScope;
}

/**
 * Two-outcome moneyline adapter over the pill click's TradeTicketContext —
 * the ONE armed-click route for moneyline pills (dashboard board and
 * GameDetail's Moneyline section), so both surfaces share the guard, the
 * capability matrix, and the owner-gated readiness blur. The ticker comes
 * from the clicked outcome's OWN synth book slot. The owner id is the
 * GAME-level card id — either team's pill click belongs to the same focused
 * game card. A soccer Draw click arrives under the draw synth's own game
 * key, which never registers a card, so it can never claim owner-match.
 */
export function tryOneClickFromMoneylineCtx({ ctx, scope }: MoneylineOneClickArgs): boolean {
  if (ctx.venue !== 'kalshi' || ctx.prefillPrice == null) return false;
  const side = ctx.initialOrderSide;
  if (side == null) return false;
  const book = ctx.teamSide === 'away' ? ctx.game.kalshi?.away : ctx.game.kalshi?.home;
  if (!book?.marketTicker) return false;
  const teamCode = ctx.teamSide === 'away' ? ctx.game.awayCode : ctx.game.homeCode;
  const inputAt = lt2ClockStamp();
  return tryOneClickFromNormalizedIntent({
    // Moneyline clicks retain the established V1 one-click grant (the
    // capability matrix above); LT2 is required only for derivative surfaces.
    capability: 'one-click',
    book,
    ownerCardId: lt2MoneylineGameCardId(scope.keyPrefix, ctx.game.key),
    intent: {
      ticker: book.marketTicker,
      side,
      priceCents: ctx.prefillPrice,
      postOnly: ctx.postOnly,
      label: `${teamCode} ${side.toUpperCase()} @ ${ctx.prefillPrice}¢`,
      inputMethod: 'mouse',
      surface: scope.surface,
      marketKind: 'moneyline',
      period: 'full',
      line: null,
      displayedPriceMs: Date.now(),
      inputAtMs: inputAt.wallMs,
      inputAtClock: inputAt,
    },
  });
}

export interface MultiOutcomeOneClickArgs {
  outcome: SeriesOutcome;
  venue: 'kalshi' | 'polymarket';
  side: 'yes' | 'no';
  priceCents?: number;
  postOnly: boolean;
}

/** Generic N-outcome moneyline adapter. Soccer is the first caller, not a
 *  special case: every future event rendered by MultiOutcomeTable inherits
 *  the same exact identity, guard, pacing, and receipt path. */
export function tryOneClickFromMultiOutcomePrice({
  outcome,
  venue,
  side,
  priceCents,
  postOnly,
}: MultiOutcomeOneClickArgs): boolean {
  if (venue !== 'kalshi' || priceCents == null) return false;

  const book = outcome.kalshi;
  // A rendered price without the exact backing Kalshi book is not enough
  // identity to place an order. Fall through to the ordinary ticket path,
  // which also disarms at the caller's safety boundary.
  if (!book?.marketTicker) return false;

  const inputAt = lt2ClockStamp();
  return tryOneClickFromNormalizedIntent({
    capability: 'one-click',
    book,
    intent: {
      ticker: book.marketTicker,
      side,
      priceCents,
      postOnly,
      label: `${outcome.name} ${side.toUpperCase()} @ ${priceCents}¢`,
      inputMethod: 'mouse',
      surface: 'dash-multi-outcome',
      marketKind: 'multi-outcome',
      period: 'full',
      line: null,
      displayedPriceMs: Date.now(),
      inputAtMs: inputAt.wallMs,
      inputAtClock: inputAt,
    },
  });
}

/** Spread synth names arrive in TWO shapes — synthesizeFromSpread's compact
 *  "Dallas Wings −3.5" / "Golden State Valkyries +3.5", and lineBetRows'
 *  detailed rewrite where the FAVORED name becomes "DAL wins by over 3.5"
 *  (no sign at all). The invariant both shapes share: the UNDERDOG name
 *  always carries a trailing '+mag' — so YES (the favored/cover outcome) is
 *  the OTHER name, and the event-convention line sign follows which slot the
 *  dog sits in (away-favored → negative). Keying YES off a '−' was the
 *  review finding: away-favored detailed rows labeled the opposite outcome. */
function spreadPerspective(game: GameRow): { yesName: string; noName: string; line: number } | null {
  const dogMag = (name: string): number | null => {
    const m = name.match(/\+(\d+(?:\.\d+)?)\s*$/);
    return m ? Number(m[1]) : null;
  };
  const awayDogMag = dogMag(game.awayName);
  if (awayDogMag != null) {
    // Away is the dog → home favored → line positive (event convention).
    return { yesName: game.homeName, noName: game.awayName, line: awayDogMag };
  }
  const homeDogMag = dogMag(game.homeName);
  if (homeDogMag != null) {
    return { yesName: game.awayName, noName: game.homeName, line: -homeDogMag };
  }
  return null;
}

/** The side-specific display name a synth GameRow carries:
 *  totals — awayName "Over 165.5" / homeName "Under 165.5" (YES = Over);
 *  spreads — via spreadPerspective (YES = the non-'+' name). */
function synthSideName(game: GameRow, kind: 'spread' | 'total', side: 'yes' | 'no'): string {
  if (kind === 'total') return side === 'yes' ? game.awayName : game.homeName;
  const p = spreadPerspective(game);
  if (p) return side === 'yes' ? p.yesName : p.noName;
  // Unrecognized name shape — fall back to slot order rather than guessing.
  return side === 'yes' ? game.awayName : game.homeName;
}

/** Best-effort numeric line off the synth names ("Over 165.5" → 165.5;
 *  spreads via spreadPerspective's sign-corrected value). Audit-only —
 *  never used for order routing. */
function synthLineValue(game: GameRow, kind: 'spread' | 'total'): number | null {
  if (kind === 'spread') return spreadPerspective(game)?.line ?? null;
  const m = game.awayName.match(/(\d+(?:\.\d+)?)\s*$/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

export function tryOneClickFromTicketCtx(args: OneClickInterceptArgs): boolean {
  const { ctx, surface, period, priceMode, focusedCardId } = args;
  const store = useOneClickStore.getState();
  const user = useAuthStore.getState().user;
  if (!store.armed) return false;
  if (!hasConfirmedGrant(user, 'oneClickAccess') || !hasConfirmedGrant(user, 'lt2Access')) return false;
  if (ctx.venue !== 'kalshi' || ctx.prefillPrice == null) return false;
  const side = ctx.initialOrderSide;
  if (side == null) return false;

  const marketKind = gameMarketKind(ctx.game);
  if (marketKind !== 'spread' && marketKind !== 'total') return false;

  const book = ctx.teamSide === 'away' ? ctx.game.kalshi?.away : ctx.game.kalshi?.home;
  if (!book?.marketTicker) return false;

  // Maker semantics, canonical (postOnly-fidelity fix): a ladder bid click is
  // a join (post-only, can't cross); a ladder ask click may cross; a pill
  // click carries the toolbar price mode's semantics. This layer decides from
  // the meta when the compact renderers supply it — the ticket handoff
  // payloads stay whatever they always were. A ctx with NO meta comes from
  // GameRows' detailed path, whose click site already derives postOnly
  // correctly (ladder bid = true) — honor it, same as V1 moneyline.
  const postOnly = ctx.meta
    ? ctx.meta.origin === 'ladder'
      ? ctx.meta.kind === 'bid'
      : priceMode === 'maker'
    : ctx.postOnly;

  const inputAt = lt2ClockStamp();
  const intent: LiveOrderIntent = {
    ticker: book.marketTicker,
    side,
    priceCents: ctx.prefillPrice,
    postOnly,
    label: `${synthSideName(ctx.game, marketKind, side)} ${side.toUpperCase()} @ ${ctx.prefillPrice}¢`,
    inputMethod: 'mouse',
    surface,
    marketKind,
    period,
    line: synthLineValue(ctx.game, marketKind),
    focusedCardId: focusedCardId ?? null,
    displayedPriceMs: Date.now(),
    inputAtMs: inputAt.wallMs,
    inputAtClock: inputAt,
  };
  return tryOneClickFromNormalizedIntent({ intent, book, capability: 'lt2' });
}
